/** 等間隔の格子。点 (i, j) の座標は (i × cellM, j × cellM)、index = j × nx + i */
export interface GridSpec {
  nx: number;
  nz: number;
  cellM: number;
}

export function gridSpec(widthM: number, depthM: number, cellM: number): GridSpec {
  return { nx: Math.round(widthM / cellM) + 1, nz: Math.round(depthM / cellM) + 1, cellM };
}

/** 双線形補間（範囲外は端の値） */
export function sampleBilinear(g: GridSpec, data: ArrayLike<number>, x: number, z: number): number {
  const fx = Math.min(Math.max(x / g.cellM, 0), g.nx - 1.000001);
  const fz = Math.min(Math.max(z / g.cellM, 0), g.nz - 1.000001);
  const i = Math.floor(fx);
  const j = Math.floor(fz);
  const tx = fx - i;
  const tz = fz - j;
  const k = j * g.nx + i;
  const h00 = data[k] as number;
  const h10 = data[k + 1] as number;
  const h01 = data[k + g.nx] as number;
  const h11 = data[k + g.nx + 1] as number;
  return (h00 * (1 - tx) + h10 * tx) * (1 - tz) + (h01 * (1 - tx) + h11 * tx) * tz;
}

/** 最も近い格子点の値（地形の種類など、補間できない値に使う） */
export function sampleNearest(g: GridSpec, data: ArrayLike<number>, x: number, z: number): number {
  const i = Math.min(Math.max(Math.round(x / g.cellM), 0), g.nx - 1);
  const j = Math.min(Math.max(Math.round(z / g.cellM), 0), g.nz - 1);
  return data[j * g.nx + i] as number;
}

/** 高さの勾配（dh/dx, dh/dz） */
export function gradientAt(
  g: GridSpec,
  height: ArrayLike<number>,
  x: number,
  z: number,
): { gx: number; gz: number } {
  const e = g.cellM;
  const gx = (sampleBilinear(g, height, x + e, z) - sampleBilinear(g, height, x - e, z)) / (2 * e);
  const gz = (sampleBilinear(g, height, x, z + e) - sampleBilinear(g, height, x, z - e)) / (2 * e);
  return { gx, gz };
}

/** 傾き（度） */
export function slopeDegAt(g: GridSpec, height: ArrayLike<number>, x: number, z: number): number {
  const { gx, gz } = gradientAt(g, height, x, z);
  return (Math.atan(Math.hypot(gx, gz)) * 180) / Math.PI;
}

/** 斜面の向き：下り方向の方位（北 0、時計回り、ラジアン） */
export function aspectAt(g: GridSpec, height: ArrayLike<number>, x: number, z: number): number {
  const { gx, gz } = gradientAt(g, height, x, z);
  return Math.atan2(-gx, gz);
}
